import React, { useEffect, useState, useRef, useCallback } from "react";
import { Bell, Check, Settings, Volume2, VolumeX, Mail, X, Phone, AlertTriangle, FileText, Clock, Users, DollarSign } from "lucide-react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";

const OFFICE_PHONE = "(559) 795-5455";

// Priority-based notification sounds using Web Audio API
const playNotificationSound = (priority = "normal") => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    
    const audioCtx = new AudioContext();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    // Different sounds based on priority
    if (priority === "urgent" || priority === "critical") {
      // Urgent: Two-tone alert (higher pitch, repeated)
      oscillator.type = "square";
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // A5
      oscillator.frequency.setValueAtTime(1046, audioCtx.currentTime + 0.15); // C6
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime + 0.3); // A5
      oscillator.frequency.setValueAtTime(1046, audioCtx.currentTime + 0.45); // C6
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.6);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.6);
    } else {
      // Normal: Pleasant ding
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(659, audioCtx.currentTime); // E5
      oscillator.frequency.setValueAtTime(784, audioCtx.currentTime + 0.1); // G5
      gainNode.gain.setValueAtTime(0.25, audioCtx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.01, audioCtx.currentTime + 0.4);
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 0.4);
    }
  } catch (e) {
    console.log("Audio not supported");
  }
};

// Get icon for notification type
const getNotificationIcon = (type) => {
  if (type?.includes("callout")) return <AlertTriangle className="w-4 h-4 text-red-500" />;
  if (type?.includes("work_order")) return <FileText className="w-4 h-4 text-blue-500" />;
  if (type?.includes("timesheet")) return <Clock className="w-4 h-4 text-amber-500" />;
  if (type?.includes("intake") || type?.includes("assessment")) return <Users className="w-4 h-4 text-emerald-500" />;
  if (type?.includes("billing") || type?.includes("payroll")) return <DollarSign className="w-4 h-4 text-purple-500" />;
  return <Bell className="w-4 h-4 text-gray-500" />;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    sound_enabled: true,
    disabled_types: [],
  });
  const [loading, setLoading] = useState(false);
  const lastUnreadRef = useRef(0);
  const initialLoadRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const notificationList = data.notifications || data.items || [];
      const unreadCount = data.unread_count ?? data.unread ?? notificationList.filter(n => !n.read).length;
      
      setItems(notificationList);
      setUnread(unreadCount);
      
      // Play sound if new notifications arrived (and not initial load)
      if (!initialLoadRef.current && unreadCount > lastUnreadRef.current && preferences.sound_enabled) {
        // Find the newest notification to determine priority
        const newestNotification = notificationList.find(n => !n.read);
        const priority = newestNotification?.priority || "normal";
        playNotificationSound(priority);
      }
      
      lastUnreadRef.current = unreadCount;
      initialLoadRef.current = false;
    } catch {
      /* ignore */
    }
  }, [preferences.sound_enabled]);

  const loadPreferences = async () => {
    try {
      const { data } = await api.get("/notifications/preferences");
      setPreferences({
        email_enabled: data.email_enabled ?? true,
        sound_enabled: data.sound_enabled ?? true,
        disabled_types: data.disabled_types || [],
      });
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    loadPreferences();
    // Poll every 30 seconds for real-time feel
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      load();
    } catch {
      /* ignore */
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      load();
    } catch {
      /* ignore */
    }
  };

  const updatePreferences = async (key, value) => {
    setLoading(true);
    try {
      await api.put("/notifications/preferences", {
        ...preferences,
        [key]: value,
      });
      setPreferences(prev => ({ ...prev, [key]: value }));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id);
    setOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-secondary transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setShowPrefs(false); }} />
          <div 
            className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
            data-testid="notification-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#0D1B33] to-[#176DFF]">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-white" />
                <span className="font-semibold text-sm text-white">Notifications</span>
                {unread > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    {unread} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowPrefs(!showPrefs)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  title="Notification Settings"
                >
                  <Settings className="w-4 h-4 text-white/80" />
                </button>
                {items.length > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-white/80 hover:text-white px-2 py-1 rounded hover:bg-white/10 transition-colors"
                    data-testid="mark-all-read"
                  >
                    <Check className="w-3.5 h-3.5 inline mr-1" /> Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Preferences Panel */}
            {showPrefs && (
              <div className="p-4 border-b border-gray-100 bg-gray-50">
                <h4 className="text-sm font-semibold text-[#0D1B33] mb-3">Notification Preferences</h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#64748B]" />
                      <span className="text-sm text-[#0D1B33]">Email notifications</span>
                    </div>
                    <button
                      onClick={() => updatePreferences("email_enabled", !preferences.email_enabled)}
                      disabled={loading}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        preferences.email_enabled ? "bg-[#176DFF]" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          preferences.email_enabled ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      {preferences.sound_enabled ? (
                        <Volume2 className="w-4 h-4 text-[#64748B]" />
                      ) : (
                        <VolumeX className="w-4 h-4 text-[#64748B]" />
                      )}
                      <span className="text-sm text-[#0D1B33]">Sound alerts</span>
                    </div>
                    <button
                      onClick={() => updatePreferences("sound_enabled", !preferences.sound_enabled)}
                      disabled={loading}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        preferences.sound_enabled ? "bg-[#176DFF]" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          preferences.sound_enabled ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </label>
                  <button
                    onClick={() => playNotificationSound("urgent")}
                    className="text-xs text-[#176DFF] hover:underline"
                  >
                    Test notification sound
                  </button>
                </div>
              </div>
            )}

            {/* Office Phone Banner */}
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
              <Phone className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-800">
                Office: <a href={`tel:${OFFICE_PHONE.replace(/[^\d]/g, "")}`} className="font-semibold hover:underline">{OFFICE_PHONE}</a>
              </span>
            </div>

            {/* Notification List */}
            <div className="max-h-[380px] overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <Bell className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-[#64748B]">You&apos;re all caught up!</p>
                  <p className="text-xs text-gray-400 mt-1">New notifications will appear here</p>
                </div>
              ) : (
                items.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !n.read ? "bg-[#176DFF]/[0.03]" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {getNotificationIcon(n.notification_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {!n.read && (
                              <span className="w-2 h-2 rounded-full bg-[#176DFF] shrink-0" />
                            )}
                            <span className="text-sm font-medium text-[#0D1B33] line-clamp-1">
                              {n.title}
                            </span>
                          </div>
                          {n.priority === "urgent" || n.priority === "critical" ? (
                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium shrink-0">
                              URGENT
                            </span>
                          ) : null}
                        </div>
                        {n.message && (
                          <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2">
                            {n.message}
                          </p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                <Link
                  to="/organization/settings"
                  onClick={() => setOpen(false)}
                  className="text-xs text-[#176DFF] hover:underline"
                >
                  Manage notification settings →
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
